/**
 * HelpPanel Component
 * 
 * Provides a collapsible help panel for admin portal sections.
 * Shows contextual help, tips, and links to documentation.
 */

import React, { useState } from 'react';

export const SECTION_HELP = {
  dashboard: {
    title: 'Dashboard Help',
    description: 'The admin dashboard provides an overview of key metrics and system health.',
    tips: [
      'Click on any metric card to navigate to the detailed view.',
      'System health indicators update every 5 minutes.',
      'Recent activity shows the last 10 admin actions.',
    ],
  },
  
  users: {
    title: 'User Management Help',
    description: 'Manage user accounts, view details, and perform administrative actions.',
    tips: [
      'Search by email, name, phone number, or user ID.',
      'SuperAdmin role required to edit users, disable accounts, or reset passwords.',
      'Disabled users cannot log in but their data is preserved.',
      'All modifications are logged via Audit Logger.',
    ],
  },
  
  subscriptions: {
    title: 'Subscription Management Help',
    description: 'View and manage user subscriptions, apply credits, and handle cancellations.',
    tips: [
      'MRR (Monthly Recurring Revenue) is calculated from active subscriptions.',
      'Extending a subscription adds days to the current end date.',
      'Credits can be applied for customer service issues or promotions.',
      'All changes are logged via Audit Logger.',
    ],
  },
  
  payments: {
    title: 'Payment Analytics Help',
    description: 'View payment metrics, transaction history, and process refunds.',
    tips: [
      'Revenue metrics update in near real-time.',
      'SuperAdmin role required to process refunds.',
      'Export transactions for accounting and reconciliation.',
      'All actions are logged via Audit Logger.',
    ],
  },
  
  tickets: {
    title: 'Support Tickets Help',
    description: 'Manage customer support tickets and track resolutions.',
    tips: [
      'Filter tickets by status, priority, category, and assignee.',
      'All ticket actions are logged via Audit Logger.',
      'Use internal notes for team communication.',
      'Assign tickets to specific admins for tracking.',
    ],
  },
  
  audit: {
    title: 'Audit Logs Help',
    description: 'View and export admin action audit logs for compliance.',
    tips: [
      'SuperAdmin role required to view all audit logs.',
      'Regular admins can only see their own actions.',
      'Export as CSV for compliance reporting.',
      'Filter by admin, action, resource, or date range.',
    ],
  },
};

const HelpPanel = ({ section, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const helpContent = SECTION_HELP[section];
  
  if (!helpContent) {
    return null;
  }

  return (
    <div className={`admin-help-panel ${isExpanded ? 'admin-help-panel--expanded' : ''}`}>
      <button 
        className="admin-help-panel__toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={`help-panel-content-${section}`}
      >
        <span className="admin-help-panel__toggle-icon">
          {isExpanded ? '−' : '?'}
        </span>
        <span className="admin-help-panel__toggle-text">
          {isExpanded ? 'Hide Help' : 'Show Help'}
        </span>
      </button>
      
      {isExpanded && (
        <div 
          id={`help-panel-content-${section}`}
          className="admin-help-panel__content"
        >
          <h3 className="admin-help-panel__title">{helpContent.title}</h3>
          <p className="admin-help-panel__description">{helpContent.description}</p>
          
          {helpContent.tips && helpContent.tips.length > 0 && (
            <div className="admin-help-panel__section">
              <h4 className="admin-help-panel__section-title">💡 Tips</h4>
              <ul className="admin-help-panel__tips">
                {helpContent.tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpPanel;
